/**
 * Typed dashboard client over fetch.
 *
 * Surface: `/v1/app/*` (session-cookie authed) + `/v1/beacon/*` (credentialless,
 * fired from the install-test button + the internal debug page).
 *
 * Shape is hand-typed against `apps/api/openapi.json` + the route serialisers in
 * `apps/api/src/routes/*.ts`. A typed-client codegen may land later; until then
 * the hand-typed shapes here are the contract.
 *
 * Conventions:
 *   - Every authed request sends `credentials: 'include'` so the session cookie
 *     reaches `api.clickt.link` (Vite dev proxy keeps it first-party).
 *   - Never sends `workspace_id` in body/query (tenancy is server-derived).
 *   - 4xx/5xx → throws `ApiError`. Components map ApiError.status to UI state.
 */

import type {
  ApiErrorPayload,
  ChannelTrackingLinkDefaults,
  CreateLinkCampaignInput,
  CreateSalesSiteInput,
  CreateSalesSiteProductInput,
  CreateSalesSiteProductResponse,
  LinkBatchSkip,
  LinkCampaign,
  ProductBreakdownRow,
  ProductKind,
  ProductVideoRow,
  SalesSite,
  SalesSiteDetail,
  SalesSiteProduct,
  SalesSiteRollupBucket,
  SalesSiteRollupPeriod,
  TrackingLinkSurface,
  UpdateChannelInput,
  UpdateSalesSiteInput,
  UpdateSalesSiteProductInput,
  VerifyPollResponse,
  VerifyStartResponse,
  VideoState,
  VideoStateFilter,
  VideoTimeseriesPeriod,
} from '@clickt/shared';

export type {
  CreateSalesSiteProductInput,
  CreateSalesSiteProductResponse,
  ProductBreakdownRow,
  ProductVideoRow,
  SalesSiteProduct,
  UpdateSalesSiteProductInput,
};

/**
 * Base origin for `/v1/app/*` calls. Prod bakes `https://api.clickt.link`
 * (cross-subdomain, matching auth-client.ts + the `/v1/app/*` CORS allowlist).
 * Dev leaves VITE_API_ORIGIN unset and falls back to the dashboard origin so
 * Vite's `/v1` proxy forwards to the API. Absolute URLs (beacons) bypass this.
 */
const API_BASE =
  import.meta.env.VITE_API_ORIGIN ??
  (typeof window === 'undefined' ? 'http://localhost:3001' : window.location.origin);

export class ApiError extends Error {
  readonly payload: ApiErrorPayload;
  readonly status: number;
  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  init?: { contentType?: string; signal?: AbortSignal },
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  let payload: BodyInit | undefined;
  if (body !== undefined) {
    if (init?.contentType === 'text/plain') {
      headers['Content-Type'] = 'text/plain';
      payload = typeof body === 'string' ? body : JSON.stringify(body);
    } else {
      headers['Content-Type'] = 'application/json';
      payload = JSON.stringify(body);
    }
  }

  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const res = await fetch(url, {
    method,
    headers,
    body: payload,
    credentials: 'include',
    signal: init?.signal,
  });

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    // Honour both ClicktError-shaped errors and the 501 honest stubs A returns.
    let errPayload: ApiErrorPayload;
    if (json && typeof json === 'object') {
      const j = json as Record<string, unknown>;
      // 501 stubs: { error: 'not_implemented', reason: '...', tracking_id }
      if (j.error === 'not_implemented' && typeof j.reason === 'string') {
        errPayload = {
          code: 'service_unavailable',
          message: j.reason,
          details: { not_implemented: true, tracking_id: j.tracking_id },
        };
      } else if (typeof j.code === 'string') {
        errPayload = j as unknown as ApiErrorPayload;
      } else {
        errPayload = { code: 'internal', message: res.statusText || 'Request failed' };
      }
    } else {
      errPayload = { code: 'internal', message: res.statusText || 'Request failed' };
    }
    throw new ApiError(res.status, errPayload);
  }

  return json as T;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type PlanTier = 'free' | 'pro' | 'agency';
export type BillingCadence = 'monthly' | 'annual';
export type SubscriptionStatus = 'active' | 'past_due' | 'cancelled' | 'paused' | 'none';

export interface BillingCheckoutResponse {
  /** Paddle transaction id (`txn_…`) to hand to Paddle.Checkout.open(). */
  transaction_id: string;
}

export interface BillingSubscriptionResponse {
  plan: PlanTier;
  subscription_status: 'active' | 'past_due' | 'cancelled' | 'paused' | 'none';
  /** ISO timestamp the current paid period ends (renewal date). Null on free. */
  billing_period_end: string | null;
  /** ISO timestamp a scheduled cancellation takes effect. Null unless cancelling. */
  cancel_at: string | null;
  /** Billing cadence of the active subscription. Null on free. */
  billing_cadence: BillingCadence | null;
  /** A Paddle customer exists (unlocks card-on-file + history sections). */
  has_billing_account: boolean;
  /** A Paddle subscription exists (unlocks change/cancel/resume). */
  has_subscription: boolean;
  /** Launch comp (T-168): ISO instant the free Pro month ends. While in the
   *  future (and plan is free with no subscription) billing is display-only.
   *  Note `plan` here stays the RAW column — a comped workspace reads 'free'. */
  comp_until: string | null;
}

export interface BillingChangePreviewResponse {
  target: { tier: 'pro' | 'agency'; cadence: BillingCadence };
  /** What confirming would do today: a prorated charge or a credit. */
  result: { action: 'charge' | 'credit'; amount_minor: number; currency: string };
  /**
   * Itemized lines for the confirm dialog: prorated target plan + tax, minus
   * credit for unused time on the current plan. tax_minor is null when Paddle
   * gave no tax split (pure-credit changes); breakdown itself is null when
   * Paddle returned neither an immediate transaction nor an update summary.
   */
  breakdown: {
    currency: string;
    new_plan_minor: number;
    tax_minor: number | null;
    credit_minor: number;
    due_today_minor: number;
  } | null;
  next_billed_at: string | null;
  recurring_amount_minor: number | null;
  /** Tax included in recurring_amount_minor; label "incl. tax" only when > 0. */
  recurring_tax_minor: number | null;
  /** True when a scheduled cancel exists; confirming also resumes. */
  resumes_subscription: boolean;
}

export interface BillingChangeResponse {
  status: 'accepted';
  resumed_scheduled_cancel: boolean;
}

export interface BillingCancelResponse {
  status: 'accepted' | 'already_scheduled';
  effective_at: string | null;
}

export interface BillingResumeResponse {
  status: 'accepted' | 'not_scheduled';
}

export interface BillingPaymentMethodResponse {
  payment_method: {
    type: string;
    saved_at: string | null;
    card: {
      brand: string | null;
      last4: string | null;
      expiry_month: number | null;
      expiry_year: number | null;
    } | null;
  } | null;
}

export interface BillingHistoryRow {
  transaction_id: string;
  billed_at: string | null;
  status: string;
  amount_minor: number;
  /** Credit applied (unused time from a downgrade). Explains $0 rows. */
  credit_minor: number;
  currency: string;
  description: string;
  invoice_available: boolean;
}

export interface BillingHistoryResponse {
  transactions: BillingHistoryRow[];
  has_more: boolean;
}

export interface MockFlipResponse {
  plan: PlanTier;
  subscription_status: string;
  billing_cadence: BillingCadence | null;
  billing_period_end: string | null;
  cancel_at: string | null;
}

export interface User {
  id: string;
  email: string;
  display_name: string | null;
  email_verified: boolean;
  /** T-170: on the ADMIN_USER_IDS allowlist. Drives the Admin nav section only;
   *  the API 404s /v1/app/admin/* for anyone else regardless. */
  is_platform_admin: boolean;
}

// ── Founder admin (T-170) ────────────────────────────────────────────────────
// Shapes mirror apps/api/src/routes/admin.ts. Read-only surface.

export type AdminStage =
  | 'signed_up'
  | 'channel_connected'
  | 'links_created'
  | 'tracking_live'
  | 'converting';

export interface AdminWorkspace {
  id: string;
  name: string;
  plan: PlanTier;
  /** Launch-comp overlay applied (what the product treats them as). */
  effective_plan: PlanTier;
  subscription_status: SubscriptionStatus;
  billing_cadence: BillingCadence | null;
  billing_period_end: string | null;
  cancel_at: string | null;
  comp_until: string | null;
}

export interface AdminUsage {
  channels: number;
  videos: number;
  links: number;
  sales_sites: number;
  sales_sites_verified: number;
  pageviews_7d: number;
  conversions_total: number;
  /** Most recent event inside the last 30 days; null = none in that window. */
  last_event_at: string | null;
}

/** Lead-quality signals. Channel-level counts are YouTube's own numbers,
 *  snapshotted at connect / resync and summed over connected channels; null
 *  until a channel that predates the snapshot columns is resynced. */
export interface AdminYouTube {
  videos: number | null;
  subscribers: number | null;
  /** Lifetime channel views. */
  views: number | null;
  /** Sum of view counts over the videos we imported (always available). */
  imported_views: number;
  top_video_views: number | null;
}

export interface AdminPerson {
  id: string;
  email: string;
  name: string | null;
  display_name: string | null;
  email_verified: boolean;
  created_at: string;
  /** Session row refresh; Better-Auth bumps it at most once a day. */
  last_seen_at: string | null;
  last_login_at: string | null;
  stage: AdminStage;
  workspace: AdminWorkspace | null;
  /** Oldest connected channel; the row names it, extras show as "+N". */
  primary_channel: { title: string; handle: string | null } | null;
  usage: AdminUsage;
  youtube: AdminYouTube;
}

export type AdminSort = 'signed_up' | 'subscribers' | 'videos' | 'views' | 'last_active';

export interface AdminUsersParams {
  q?: string;
  plan?: PlanTier;
  status?: SubscriptionStatus;
  stage?: AdminStage;
  signed_up_within?: number;
  sort?: AdminSort;
  limit?: number;
  offset?: number;
}

export interface AdminUsersResponse {
  users: AdminPerson[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminOverview {
  signups: { total: number; last_7d: number; prev_7d: number; unverified: number };
  users_active_7d: number;
  paying: { pro: number; agency: number };
  past_due: number;
  cancel_pending: number;
  workspaces_with_events_7d: number;
  generated_at: string;
}

export interface AdminUserDetail extends AdminPerson {
  billing: { paddle_customer_id: string | null; paddle_subscription_id: string | null };
  channels: Array<{
    id: string;
    title: string;
    handle: string | null;
    youtube_channel_id: string;
    oauth_connected_at: string | null;
    oauth_disconnected_at: string | null;
    last_import_at: string | null;
    import_status: string;
    /** Videos we imported. */
    video_count: number;
    /** YouTube's own count at connect / resync; null until resynced. */
    youtube_video_count: number | null;
    subscribers: number | null;
    channel_views: number | null;
    imported_views: number;
    top_video_views: number | null;
    likes: number;
  }>;
  sales_sites: Array<{
    id: string;
    name: string;
    platform: string;
    origin: string;
    verified_at: string | null;
    last_test_at: string | null;
    last_test_status: string | null;
    retired_at: string | null;
  }>;
  activity: Array<{
    id: string;
    action: string;
    target_type: string;
    target_id: string;
    /** 'user' when the person did it themselves, else the system actor label. */
    actor: string;
    created_at: string;
  }>;
  sessions: Array<{
    id: string;
    created_at: string | null;
    updated_at: string | null;
    expires_at: string | null;
    user_agent: string | null;
  }>;
}

export interface Membership {
  role: 'owner' | 'admin' | 'viewer';
  workspace_id: string;
  workspace_name: string;
  workspace_public_id: string;
  org_id: string;
  /** EFFECTIVE plan: the server applies the launch-comp overlay (T-168), so a
   *  comped workspace reads 'pro' here until comp_until passes. */
  plan: PlanTier;
  /** Launch comp (T-168): ISO instant the free Pro month ends. Null when the
   *  workspace was created after the window closed. */
  comp_until: string | null;
}

export interface MeResponse {
  user: User;
  memberships: Membership[];
}

export interface SetActiveWorkspaceResponse {
  active_workspace_id: string;
  plan: PlanTier;
  role: 'owner' | 'admin' | 'viewer';
}

export interface Channel {
  id: string;
  youtube_channel_id: string;
  title: string;
  handle: string | null;
  avatar_url: string | null;
  oauth_connected_at: string | null;
  /** ISO timestamp of the most recent soft-disconnect. NULL = currently connected (or never connected). */
  oauth_disconnected_at: string | null;
  last_import_at: string | null;
  /** Number of videos owned by this channel (workstream A3). */
  video_count: number;
  /** Distinct sessions that landed via a tracking link pointing at any video on this channel. */
  total_clicks: number;
  /** Dashboard redesign R5: distinct sessions in the last 7 days (used by client-side delta cards). */
  total_clicks_7d: number;
  /** Dashboard redesign R5: distinct sessions in the 7 days before the last 7. Pairs with total_clicks_7d. */
  total_clicks_prev_7d: number;
  /** Paywalled on free/pro plans (stripped). Attributed conversions across all of this channel's videos. */
  total_conversions?: number;
  /** Paywalled. R5 7-day window. */
  total_conversions_7d?: number;
  /** Paywalled. R5 previous 7-day window. */
  total_conversions_prev_7d?: number;
  /** Paywalled on free/pro plans (stripped). Minor-unit revenue total across this channel's videos. */
  total_revenue_minor?: number;
  /** Paywalled. R5 7-day window revenue, minor units. */
  total_revenue_minor_7d?: number;
  /** Paywalled. R5 previous 7-day window revenue, minor units. */
  total_revenue_minor_prev_7d?: number;
  /** R9 per-channel link defaults; NULL = no overrides. Not surfaced on the list endpoint today, included here for future use. */
  tracking_link_defaults?: ChannelTrackingLinkDefaults | null;
  /**
   * Video-import state machine (slice E2).
   *   pending    no import attempted yet
   *   running    page 1 in flight (sync, blocks 1–5s during OAuth/resync)
   *   importing  page 1 done; pages 2+ outstanding via queue
   *   complete   all pages done
   *   failed     terminal failure; show import_error + Retry CTA
   */
  import_status: ChannelImportStatus;
  /** Human-readable error when import_status='failed'. */
  import_error: string | null;
  /** Pages successfully processed in the current run (each page ≈ 50 videos). */
  import_pages_done: number;
  /** channels.list statistics.videoCount captured at OAuth-connect time. */
  youtube_video_count_snapshot: number | null;
}

export type ChannelImportStatus = 'pending' | 'running' | 'importing' | 'complete' | 'failed';

export interface ChannelDetail {
  id: string;
  title: string;
  tracking_link_defaults: ChannelTrackingLinkDefaults | null;
  updated_at: string | null;
}

export interface PatchChannelResponse {
  channel: ChannelDetail;
}

export interface ResyncChannelResponse {
  outcome: 'done' | 'requeue' | 'retry_quota' | 'failed';
  import_status: ChannelImportStatus | null;
  import_error: string | null;
  import_pages_done: number;
  last_import_at: string | null;
}

export interface ChannelsListResponse {
  channels: Channel[];
  next_cursor: string | null;
  /**
   * T-147: workspace-level count of ALL sale conversions recorded on sales
   * pages in the last 7 days, attributed or not. Paywalled (absent on free).
   */
  seen_conversions_7d?: number;
  /** T-147: declared-price revenue across all seen sale conversions (7d). Paywalled. */
  seen_revenue_minor_7d?: number;
  /** Server envelope flag: when true, paywalled metric fields have been stripped from response rows. */
  paywalled?: boolean;
  /** Server mirror of the workspace plan (NOT for auth). */
  plan?: PlanTier;
}

export interface VideoRow {
  id: string;
  youtube_video_id: string;
  title: string;
  publish_date: string | null;
  /** Lifetime YouTube view count (snapshot at last import). Context only — not a ratio denominator. */
  views: number;
  /** Views accrued since clickt first imported the video (T-099). Denominator for revenue_per_view. */
  views_tracked: number;
  tracked_clicks: number;
  /** Dashboard redesign R2: clicks in the last 7 days (used by client-side Trending derivation). */
  tracked_clicks_7d: number;
  /** Dashboard redesign R2: clicks in the 7 days before the last 7 days. Pair with `tracked_clicks_7d`. */
  tracked_clicks_prev_7d: number;
  /** Server-derived tri-state (plan §1.1): hidden wins, then tracked, then pending. */
  state: VideoState;
  /** ISO timestamp the video was soft-hidden, NULL when visible. */
  hidden_at: string | null;
  conversions?: number;
  revenue_minor?: number;
  conversion_rate?: number | null;
  revenue_per_view?: number | null;
}

export interface VideosListResponse {
  videos: VideoRow[];
  next_cursor: string | null;
  paywalled?: boolean;
}

export interface VideoDetailResponse {
  video: {
    id: string;
    youtube_video_id: string;
    title: string;
    description: string;
    publish_date: string | null;
    channel_id: string;
    state: VideoState;
    hidden_at: string | null;
    /** Paywalled (plan §1.7): stripped on free; ISO timestamp on pro/agency. */
    last_conversion_at?: string | null;
  };
  funnel: {
    /** Lifetime YouTube view count (snapshot at last import). */
    views: number;
    /** Views accrued since clickt first imported the video (T-099). Denominator for revenue_per_view. */
    views_tracked: number;
    tracked_clicks: number;
    engaged_10s: number;
    cta_clicks: number;
    checkout_starts: number;
    conversions?: number;
    revenue_minor?: number;
    refunded: number | null;
    conversion_rate?: number | null;
    revenue_per_view?: number | null;
  };
  time_to_conversion?: {
    median_ms: number | null;
    p90_ms: number | null;
    p99_ms: number | null;
  };
  paywalled?: boolean;
}

export interface TrackingLink {
  id: string;
  short_id: string;
  workspace_id: string;
  org_id: string;
  channel_id: string | null;
  video_id: string | null;
  /** Sales site this link feeds (R12). Null only on legacy / orphaned rows. */
  sales_site_id: string | null;
  /** Product the destination lands on (T-150). Null for custom-path links. */
  product_id: string | null;
  /** The bound product's kind; a link counts conversions of its own kind, and
   * non-sale links have no revenue dimension. */
  product_kind: ProductKind | null;
  /** The bound product's current name, joined on read. Feeds `linkDisplayName`
   *  so a label-less link never goes stale behind a product rename. */
  product_name: string | null;
  destination_url: string;
  surface: TrackingLinkSurface;
  label: string | null;
  /** The campaign (folder) the link sits in under its source (T-200). Off-video links only. */
  campaign_id: string | null;
  replaces_link_id: string | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
  retired_at: string | null;
  tagged_url: string;
  /** Per-link metric: distinct sessions that landed via this link's pageview events. */
  tracked_clicks: number;
  /** Paywalled on free tier (stripped). Number of attributed conversions for this link. */
  conversions?: number;
  /** Paywalled on free tier (stripped). Revenue attributed to this link in minor units. */
  revenue_minor?: number;
  /** Paywalled on free tier (stripped). conversions / tracked_clicks. Null when no clicks. */
  conversion_rate?: number | null;
}

export interface LinksListResponse {
  links: TrackingLink[];
  next_cursor: string | null;
  paywalled?: boolean;
}

export interface LinkPreviewResponse {
  short_id: string;
  destination_url: string;
  tagged_url: string;
}

export interface CreateLinkInput {
  destination_url: string;
  surface?: TrackingLinkSurface;
  channel_id?: string | null;
  video_id?: string | null;
  /**
   * Upcoming-video link (T-086). Set true to mint a YouTube-surface link
   * before the video is published; supply `channel_id` and omit `video_id`.
   * Associate the real video later via {@link PatchLinkInput.video_id}.
   */
  pending_video?: boolean;
  /**
   * Sales site the link feeds (R12). Required. Server validates that the
   * site belongs to the caller's workspace + the link's channel, and that
   * `destination_url`'s origin matches the site's registered origin.
   */
  sales_site_id: string;
  /**
   * Product under the sales site (T-150). Optional: omit for custom paths —
   * the server still auto-matches the destination pathname against the site's
   * active products. When supplied, the destination pathname must equal the
   * product's path.
   */
  product_id?: string | null;
  /** Optional human label. Empty / whitespace gets normalised to null server-side. */
  label?: string | null;
  /** Campaign to put an off-video link in (T-200): same channel and source as the link. */
  campaign_id?: string | null;
}

/**
 * Batch create. One request per "add tracking links" sitting: the modal mints
 * one link per (product × placement), and the response says exactly which rows
 * landed and which didn't — the free-tier cap can bite partway through.
 */
export interface CreateLinksBatchInput {
  links: CreateLinkInput[];
}

export interface CreateLinksBatchResponse {
  created: TrackingLink[];
  skipped: LinkBatchSkip[];
  /** Plan headroom AFTER the batch ran. */
  capacity: LinkCapacityResponse;
  /** Present when at least one row was skipped for the plan cap. */
  upgrade_url?: string;
  paywalled?: boolean;
}

export interface LinkCapacityResponse {
  plan: string;
  /** null = unlimited (paid plans). */
  max_links: number | null;
  active_links: number;
  /** null = unlimited. */
  remaining: number | null;
}

export interface PatchLinkInput {
  label?: string | null;
  surface?: TrackingLinkSurface;
  /**
   * Move an off-video link into a campaign, or out of one with null (T-200).
   * A link changing source must leave its campaign in the same request.
   */
  campaign_id?: string | null;
  /** Associate (ULID) or clear (null) the video an upcoming-video link points at (T-086). */
  video_id?: string | null;
}

export interface VideoTimeseriesBucket {
  /** ISO date (YYYY-MM-DD). */
  day: string;
  tracked_clicks: number;
  /** Paywalled on free tier (stripped). */
  conversions?: number;
  /** Paywalled on free tier (stripped). Minor units (cents). */
  revenue_minor?: number;
}

export interface VideoTimeseriesResponse {
  period: VideoTimeseriesPeriod;
  buckets: VideoTimeseriesBucket[];
  paywalled?: boolean;
  plan?: PlanTier;
}

export interface PatchVideoResponse {
  video: {
    id: string;
    hidden: boolean;
    hidden_at: string | null;
  };
}

export interface BulkHideResponse {
  hidden_count: number;
}

export interface BulkUnhideResponse {
  unhidden_count: number;
}

export interface VideoPickerRow {
  id: string;
  youtube_video_id: string;
  title: string;
  channel_id: string;
  channel_title: string;
  publish_date: string | null;
}

export interface VideoPickerListResponse {
  videos: VideoPickerRow[];
  next_cursor: string | null;
}

export interface SalesSitesListResponse {
  /**
   * Rows carry the R12 rollup fields (linked_videos_count, active_links_count,
   * rollup_30d) so the Sales page renders per-card rollups without a per-site
   * detail fetch. rollup_30d's conversions / revenue_minor / seen_conversions /
   * seen_revenue_minor (T-147) are stripped on free tier; `paywalled` mirrors
   * that.
   */
  sales_sites: SalesSiteDetail[];
  paywalled?: boolean;
  plan?: PlanTier;
}

/**
 * Tracking link belonging to a sales site, joined with parent-video info
 * for the per-site detail page's link table (R12).
 */
export interface SalesSiteLink {
  id: string;
  short_id: string;
  destination_url: string;
  surface: TrackingLinkSurface;
  label: string | null;
  created_at: string;
  retired_at: string | null;
  channel_id: string | null;
  video_id: string | null;
  /** Product the destination lands on (T-150). Null for custom-path links. */
  product_id: string | null;
  /** The product's name, left-joined for display. */
  product_name: string | null;
  /** The product's kind; non-sale links have no revenue dimension. */
  product_kind: ProductKind | null;
  video_title: string | null;
  youtube_video_id: string | null;
  tagged_url: string;
  tracked_clicks: number;
  /** Paywalled on free tier. */
  conversions?: number;
  /** Paywalled on free tier. */
  revenue_minor?: number;
}

export interface SalesSiteLinksResponse {
  links: SalesSiteLink[];
  paywalled?: boolean;
  plan?: PlanTier;
}

export interface SalesSiteProductsResponse {
  products: SalesSiteProduct[];
}

export interface ProductBreakdownResponse {
  rows: ProductBreakdownRow[];
  paywalled?: boolean;
  plan?: PlanTier;
}

export interface ProductVideosResponse {
  rows: ProductVideoRow[];
  paywalled?: boolean;
  plan?: PlanTier;
}

export interface SalesSiteRollupResponse {
  buckets: SalesSiteRollupBucket[];
  paywalled?: boolean;
  plan?: PlanTier;
}

// ─── Bulk rewrite (workstream E slice 3, Pro+) ─────────────────────────────────

export interface UrlGroupOccurrence {
  video_id: string;
  youtube_video_id: string;
  video_title: string;
  /** The clean (cl-stripped) URL found in the description. */
  url: string;
  /** True when this URL already carries a `?cl=` token. Excluded from the
   *  default selection so a re-run doesn't re-tag what's already done. */
  already_tracked: boolean;
}

export interface UrlGroup {
  normalized_host: string;
  sample_url: string;
  video_count: number;
  /** Sales site on this host, if the workspace has one. Null = needs setup. */
  matching_sales_site_id: string | null;
  occurrences: UrlGroupOccurrence[];
}

export interface BulkRewriteProductRef {
  id: string;
  name: string;
  path: string;
  kind: ProductKind;
}

export interface BulkRewriteSalesSiteRef {
  id: string;
  name: string;
  origin: string;
  host: string | null;
  /** Active products on this site. The review UI previews the per-URL product
   *  match `/start` will stamp (exact-pathname rule, `productPathnameOf`). */
  products: BulkRewriteProductRef[];
}

export interface UrlGroupsResponse {
  url_groups: UrlGroup[];
  /** Full description per contributing video, keyed by video_id. Lets the
   *  review UI show each link in its surrounding description text without a
   *  second fetch. Only videos that produced an occurrence are included. */
  descriptions: Record<string, string>;
  total_videos_scanned: number;
  sales_sites: BulkRewriteSalesSiteRef[];
}

export interface BulkRewriteStartItem {
  video_id: string;
  sales_site_id: string;
  from_url: string;
}

export interface BulkRewriteStartResponse {
  batch_id: string;
  video_count: number;
  link_count: number;
}

/** One link this batch tagged: the clean source URL → the live tagged URL
 *  (carrying the real minted code), so the detail page can show exactly what
 *  now sits in the description. */
export interface BulkRewriteRecordRewrite {
  from: string;
  to: string;
  click_id: string;
}

export interface BulkRewriteRecord {
  video_id: string;
  youtube_video_id: string;
  video_title: string;
  /** 'pending' | 'succeeded' | 'failed' | 'rolled_back' */
  status: string;
  error: string | null;
  /** Per-link rewrites applied to this video (empty until the job succeeds). */
  rewrites: BulkRewriteRecordRewrite[];
}

export interface BulkRewriteBatchResponse {
  batch: {
    id: string;
    channel_id: string;
    /** 'in_progress' | 'completed' | 'failed' | 'rolled_back' */
    status: string;
    video_count_total: number;
    video_count_succeeded: number;
    video_count_failed: number;
    created_at: string;
    completed_at: string | null;
    rolled_back_at: string | null;
    expires_at: string | null;
  };
  records: BulkRewriteRecord[];
}

/** One row in the workspace's bulk-rewrite history list. */
export interface BulkRewriteBatchSummary {
  id: string;
  channel_id: string;
  /** Channel name, joined server-side so the list reads without a channels fetch. */
  channel_title: string;
  /** 'pending' | 'in_progress' | 'completed' | 'failed' | 'rolled_back' */
  status: string;
  video_count_total: number;
  video_count_succeeded: number;
  video_count_failed: number;
  created_at: string;
  completed_at: string | null;
  rolled_back_at: string | null;
  expires_at: string | null;
}

export interface BulkRewriteBatchListResponse {
  /** Newest first, capped at 100. */
  batches: BulkRewriteBatchSummary[];
}

// ─── Methods ──────────────────────────────────────────────────────────────────

export const api = {
  // Auth: handled by `@better-auth/react`'s authClient (see `lib/auth-client.ts`),
  // not by this typed API client. /v1/auth/* endpoints are not enumerated here.

  // Session
  me(signal?: AbortSignal) {
    return request<MeResponse>('GET', '/v1/app/me', undefined, { signal });
  },

  // Founder admin (T-170). Server-gated: 404 for anyone not on ADMIN_USER_IDS.
  adminOverview(signal?: AbortSignal) {
    return request<AdminOverview>('GET', '/v1/app/admin/overview', undefined, { signal });
  },
  adminListUsers(params: AdminUsersParams = {}, signal?: AbortSignal) {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.plan) qs.set('plan', params.plan);
    if (params.status) qs.set('status', params.status);
    if (params.stage) qs.set('stage', params.stage);
    if (params.signed_up_within !== undefined) {
      qs.set('signed_up_within', String(params.signed_up_within));
    }
    if (params.sort) qs.set('sort', params.sort);
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.offset !== undefined) qs.set('offset', String(params.offset));
    const suffix = qs.toString();
    return request<AdminUsersResponse>(
      'GET',
      `/v1/app/admin/users${suffix ? `?${suffix}` : ''}`,
      undefined,
      { signal },
    );
  },
  adminUser(userId: string, signal?: AbortSignal) {
    return request<AdminUserDetail>(
      'GET',
      `/v1/app/admin/users/${encodeURIComponent(userId)}`,
      undefined,
      { signal },
    );
  },
  setActiveWorkspace(workspaceId: string) {
    return request<SetActiveWorkspaceResponse>('POST', '/v1/app/sessions/active-workspace', {
      workspace_id: workspaceId,
    });
  },

  // OAuth: YouTube channel connect (workstream E slice 1).
  // Returns the Google consent URL the dashboard should redirect the user to.
  // The server inserts the matching oauth_states row before returning.
  //
  // `channel_id` (internal clickt id, not the YouTube id) locks the grant to a
  // specific channel row: the server resolves the row's youtube_channel_id and
  // rejects any grant whose primary doesn't match. Pass it from the per-channel
  // Reconnect button. Omit it on add-new-channel flows.
  oauthYoutubeStart(opts?: { return_to?: string; channel_id?: string }) {
    const params = new URLSearchParams();
    if (opts?.return_to) params.set('return_to', opts.return_to);
    if (opts?.channel_id) params.set('channel_id', opts.channel_id);
    const qs = params.toString();
    return request<{ url: string }>('GET', `/v1/app/oauth/youtube/start${qs ? `?${qs}` : ''}`);
  },
  disconnectChannel(channelId: string) {
    return request<undefined>('DELETE', `/v1/app/channels/${channelId}`);
  },
  /**
   * Hard-delete a channel + its videos + its links + its channel-scoped sales
   * sites. Frees a plan-cap slot. The `confirm_title` body must match the
   * channel's current title (case-insensitive trimmed) or the request 422s.
   */
  permanentlyDeleteChannel(channelId: string, confirm_title: string) {
    return request<undefined>('DELETE', `/v1/app/channels/${channelId}/permanent`, {
      confirm_title,
    });
  },
  /** Rename the channel and/or update per-channel link defaults (R9). */
  patchChannel(channelId: string, input: UpdateChannelInput) {
    return request<PatchChannelResponse>('PATCH', `/v1/app/channels/${channelId}`, input);
  },
  /** Slice E2: "fetch new videos now". Synchronous (1–5s). Idempotent. */
  resyncChannel(channelId: string) {
    return request<ResyncChannelResponse>('POST', `/v1/app/channels/${channelId}/resync`);
  },

  // Channels (workspace-scoped, takes the active workspace's id from /me)
  listChannels(workspaceId: string, opts?: { after?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (opts?.after) params.set('after', opts.after);
    if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return request<ChannelsListResponse>(
      'GET',
      `/v1/app/workspaces/${workspaceId}/channels${qs ? `?${qs}` : ''}`,
    );
  },

  // Videos
  listVideos(
    channelId: string,
    opts?: { after?: string; limit?: number; sort?: string; state?: VideoStateFilter },
  ) {
    const params = new URLSearchParams();
    if (opts?.after) params.set('after', opts.after);
    if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts?.sort) params.set('sort', opts.sort);
    if (opts?.state) params.set('state', opts.state);
    const qs = params.toString();
    return request<VideosListResponse>(
      'GET',
      `/v1/app/channels/${channelId}/videos${qs ? `?${qs}` : ''}`,
    );
  },
  /**
   * List all videos in the workspace (picker-shaped: title, channel title, etc.).
   * `q` filters by case-insensitive title substring; `channel_id` narrows to
   * one channel. Cap 500 per page.
   */
  listAllVideos(opts?: { after?: string; limit?: number; q?: string; channel_id?: string }) {
    const params = new URLSearchParams();
    if (opts?.after) params.set('after', opts.after);
    if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts?.q) params.set('q', opts.q);
    if (opts?.channel_id) params.set('channel_id', opts.channel_id);
    const qs = params.toString();
    return request<VideoPickerListResponse>('GET', `/v1/app/videos${qs ? `?${qs}` : ''}`);
  },
  getVideo(videoId: string) {
    return request<VideoDetailResponse>('GET', `/v1/app/videos/${videoId}`);
  },
  /** Toggle hidden state on a video. Audit-logged server-side. */
  patchVideo(videoId: string, input: { hidden: boolean }) {
    return request<PatchVideoResponse>('PATCH', `/v1/app/videos/${videoId}`, input);
  },
  /** Daily buckets for the Performance chart on /app/videos/:id. */
  getVideoTimeseries(videoId: string, opts?: { period?: VideoTimeseriesPeriod }) {
    const params = new URLSearchParams();
    if (opts?.period) params.set('period', opts.period);
    const qs = params.toString();
    return request<VideoTimeseriesResponse>(
      'GET',
      `/v1/app/videos/${videoId}/timeseries${qs ? `?${qs}` : ''}`,
    );
  },
  /**
   * Channel-level daily buckets (same shape as the video timeseries), summed
   * across the channel's video-bound tracking links. Feeds the Overview
   * MetricCard sparklines (T-100).
   */
  getChannelTimeseries(channelId: string, opts?: { period?: VideoTimeseriesPeriod }) {
    const params = new URLSearchParams();
    if (opts?.period) params.set('period', opts.period);
    const qs = params.toString();
    return request<VideoTimeseriesResponse>(
      'GET',
      `/v1/app/channels/${channelId}/timeseries${qs ? `?${qs}` : ''}`,
    );
  },
  /**
   * Mass-hide a channel's videos. Three shapes:
   *   { video_ids }              hide a specific subset
   *   { all_existing: true }     hide every video (Start-fresh onboarding fork)
   *   { publish_date_before }    hide every video published before this ISO
   *                              timestamp (Videos-tab cleanup control)
   */
  bulkHideChannelVideos(
    channelId: string,
    input: { video_ids: string[] } | { all_existing: true } | { publish_date_before: string },
  ) {
    return request<BulkHideResponse>(
      'POST',
      `/v1/app/channels/${channelId}/videos/bulk-hide`,
      input,
    );
  },
  /** Companion to bulk-hide for the undo-toast path. */
  bulkUnhideChannelVideos(
    channelId: string,
    input: { video_ids: string[] } | { all_existing: true },
  ) {
    return request<BulkUnhideResponse>(
      'POST',
      `/v1/app/channels/${channelId}/videos/bulk-unhide`,
      input,
    );
  },

  // Links
  listLinks(opts?: {
    after?: string;
    limit?: number;
    include_retired?: boolean;
    video_id?: string;
    sales_site_id?: string;
    channel_id?: string;
    /** Only "upcoming-video" links: YouTube surface, not yet bound to a video (T-086). */
    pending?: boolean;
    /** Only off-video links: channel page, newsletter, social bio, … (T-200). */
    off_video?: boolean;
  }) {
    const params = new URLSearchParams();
    if (opts?.after) params.set('after', opts.after);
    if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts?.include_retired) params.set('include_retired', 'true');
    if (opts?.video_id) params.set('video_id', opts.video_id);
    if (opts?.sales_site_id) params.set('sales_site_id', opts.sales_site_id);
    if (opts?.channel_id) params.set('channel_id', opts.channel_id);
    if (opts?.pending) params.set('pending', 'true');
    if (opts?.off_video) params.set('off_video', 'true');
    const qs = params.toString();
    return request<LinksListResponse>('GET', `/v1/app/links${qs ? `?${qs}` : ''}`);
  },
  createLink(input: CreateLinkInput) {
    return request<TrackingLink>('POST', '/v1/app/links', input);
  },
  /** Create many links in one request; always resolves (per-row outcomes are in
   *  the body) unless the plan cap was already full, which still throws 402. */
  createLinksBatch(input: CreateLinksBatchInput) {
    return request<CreateLinksBatchResponse>('POST', '/v1/app/links/batch', input);
  },
  getLinkCapacity() {
    return request<LinkCapacityResponse>('GET', '/v1/app/links/capacity');
  },
  getLink(id: string) {
    return request<TrackingLink>('GET', `/v1/app/links/${id}`);
  },
  patchLink(id: string, input: PatchLinkInput) {
    return request<TrackingLink>('PATCH', `/v1/app/links/${id}`, input);
  },
  retireLink(id: string) {
    return request<undefined>('DELETE', `/v1/app/links/${id}`);
  },
  previewLink(id: string) {
    return request<LinkPreviewResponse>('GET', `/v1/app/links/${id}/preview`);
  },
  /** Daily buckets for one link, counted the way its row is (T-200). */
  getLinkTimeseries(id: string, opts?: { period?: VideoTimeseriesPeriod }) {
    const params = new URLSearchParams();
    if (opts?.period) params.set('period', opts.period);
    const qs = params.toString();
    return request<VideoTimeseriesResponse>(
      'GET',
      `/v1/app/links/${id}/timeseries${qs ? `?${qs}` : ''}`,
    );
  },

  // Campaigns (T-200): folders for off-video links, one source each.
  listLinkCampaigns(channelId: string) {
    return request<{ campaigns: LinkCampaign[] }>(
      'GET',
      `/v1/app/links/campaigns?channel_id=${encodeURIComponent(channelId)}`,
    );
  },
  /** 409 when the source already has a campaign with that name, ignoring case. */
  createLinkCampaign(input: CreateLinkCampaignInput) {
    return request<LinkCampaign>('POST', '/v1/app/links/campaigns', input);
  },
  renameLinkCampaign(id: string, name: string) {
    return request<LinkCampaign>('PATCH', `/v1/app/links/campaigns/${id}`, { name });
  },
  /** Its links stay, outside any campaign. */
  deleteLinkCampaign(id: string) {
    return request<undefined>('DELETE', `/v1/app/links/campaigns/${id}`);
  },

  // Sales sites (channel-scoped landing-page registrations, plan §1.3 / R8).
  listChannelSalesSites(channelId: string) {
    return request<SalesSitesListResponse>('GET', `/v1/app/channels/${channelId}/sales-sites`);
  },
  createSalesSite(channelId: string, input: CreateSalesSiteInput) {
    return request<SalesSite>('POST', `/v1/app/channels/${channelId}/sales-sites`, input);
  },
  /**
   * Detail variant. Returns the standard SalesSite fields plus R12 derived
   * rollup fields (`linked_videos_count`, `active_links_count`, `rollup_30d`).
   * The rollup_30d sub-object's conversions / revenue_minor / seen_* fields
   * (T-147) are stripped on free tier; the envelope's `paywalled` flag
   * mirrors that.
   */
  getSalesSite(siteId: string) {
    return request<SalesSiteDetail & { paywalled?: boolean; plan?: PlanTier }>(
      'GET',
      `/v1/app/sales-sites/${siteId}`,
    );
  },
  /** Active tracking links feeding this sales site, joined with parent video. */
  listSalesSiteLinks(siteId: string) {
    return request<SalesSiteLinksResponse>('GET', `/v1/app/sales-sites/${siteId}/links`);
  },
  /** Daily rollup buckets for the per-site performance chart. */
  getSalesSiteRollup(siteId: string, opts?: { period?: SalesSiteRollupPeriod }) {
    const period = opts?.period ?? '30d';
    return request<SalesSiteRollupResponse>(
      'GET',
      `/v1/app/sales-sites/${siteId}/rollup?period=${period}`,
    );
  },
  patchSalesSite(siteId: string, input: UpdateSalesSiteInput) {
    return request<SalesSite>('PATCH', `/v1/app/sales-sites/${siteId}`, input);
  },
  retireSalesSite(siteId: string) {
    return request<undefined>('DELETE', `/v1/app/sales-sites/${siteId}`);
  },

  // Products under a sales site (T-150).
  listSalesSiteProducts(siteId: string) {
    return request<SalesSiteProductsResponse>('GET', `/v1/app/sales-sites/${siteId}/products`);
  },
  /** Create; the response's matched_links_count = existing links back-matched by path. */
  createSalesSiteProduct(siteId: string, input: CreateSalesSiteProductInput) {
    return request<CreateSalesSiteProductResponse>(
      'POST',
      `/v1/app/sales-sites/${siteId}/products`,
      input,
    );
  },
  patchSalesSiteProduct(siteId: string, productId: string, input: UpdateSalesSiteProductInput) {
    return request<SalesSiteProduct>(
      'PATCH',
      `/v1/app/sales-sites/${siteId}/products/${productId}`,
      input,
    );
  },
  retireSalesSiteProduct(siteId: string, productId: string) {
    return request<undefined>('DELETE', `/v1/app/sales-sites/${siteId}/products/${productId}`);
  },
  /** Per-product metrics for the site detail page. Paywall envelope on free tier. */
  getSalesSiteProductBreakdown(siteId: string) {
    return request<ProductBreakdownResponse>(
      'GET',
      `/v1/app/sales-sites/${siteId}/products/breakdown`,
    );
  },
  /** Which videos feed a product (top 25). Paywall envelope on free tier. */
  getSalesSiteProductVideos(siteId: string, productId: string) {
    return request<ProductVideosResponse>(
      'GET',
      `/v1/app/sales-sites/${siteId}/products/${productId}/videos`,
    );
  },
  /**
   * Begin an install-verify session. Returns the server-issued `since` cutoff
   * the dashboard echoes back on every poll so we only count pageview events
   * that landed after the user clicked Verify.
   */
  startSalesSiteVerify(siteId: string) {
    return request<VerifyStartResponse>('POST', `/v1/app/sales-sites/${siteId}/verify/start`);
  },
  /**
   * Poll for a matching pageview. Returns `pending` until the pixel fires a
   * pageview from the registered origin (with ts >= since), then `pass` with
   * the freshly-verified sales_site row. Dashboard caps the loop at 30s.
   */
  pollSalesSiteVerify(siteId: string, since: string, signal?: AbortSignal) {
    return request<VerifyPollResponse>(
      'GET',
      `/v1/app/sales-sites/${siteId}/verify/poll?since=${encodeURIComponent(since)}`,
      undefined,
      { signal },
    );
  },

  // Bulk rewrite (Pro+). Scan a channel's descriptions, tag matched links in batch.
  listBulkRewriteBatches(workspaceId: string, signal?: AbortSignal) {
    return request<BulkRewriteBatchListResponse>(
      'GET',
      `/v1/app/bulk-rewrite?workspace_id=${encodeURIComponent(workspaceId)}`,
      undefined,
      { signal },
    );
  },
  bulkRewriteUrlGroups(channelId: string, signal?: AbortSignal) {
    return request<UrlGroupsResponse>(
      'GET',
      `/v1/app/bulk-rewrite/url-groups?channel_id=${encodeURIComponent(channelId)}`,
      undefined,
      { signal },
    );
  },
  startBulkRewrite(input: { channel_id: string; items: BulkRewriteStartItem[] }) {
    return request<BulkRewriteStartResponse>('POST', '/v1/app/bulk-rewrite/start', input);
  },
  getBulkRewriteBatch(batchId: string, signal?: AbortSignal) {
    return request<BulkRewriteBatchResponse>('GET', `/v1/app/bulk-rewrite/${batchId}`, undefined, {
      signal,
    });
  },
  rollbackBulkRewrite(batchId: string) {
    return request<{ batch_id: string; video_count: number }>(
      'POST',
      `/v1/app/bulk-rewrite/${batchId}/rollback`,
    );
  },

  // Billing (Paddle). Checkout creates a transaction the dashboard opens via the
  // Paddle.js overlay; the plan flip itself lands later through the webhook —
  // mutations here never return plan state, callers reconcile by polling
  // billingSubscription (use-billing-reconcile.ts).
  billingCheckout(input: { tier: 'pro' | 'agency'; cadence: BillingCadence }) {
    return request<BillingCheckoutResponse>('POST', '/v1/app/billing/checkout', input);
  },
  /**
   * Current plan + subscription state (read from our columns; webhook-maintained).
   * `fresh: true` asks the server to pull from Paddle first — the webhook-lag
   * rescue used by the reconcile poll's final attempt.
   */
  billingSubscription(signal?: AbortSignal, opts?: { fresh?: boolean }) {
    return request<BillingSubscriptionResponse>(
      'GET',
      `/v1/app/billing/subscription${opts?.fresh ? '?fresh=1' : ''}`,
      undefined,
      { signal },
    );
  },
  /** Exact charge/credit a plan change would cause today. Never mutates. */
  billingChangePreview(input: { tier: 'pro' | 'agency'; cadence: BillingCadence }) {
    return request<BillingChangePreviewResponse>('POST', '/v1/app/billing/change/preview', input);
  },
  /** Swap the subscription's price (tier/cadence). Clears a scheduled cancel first. */
  billingChange(input: { tier: 'pro' | 'agency'; cadence: BillingCadence }) {
    return request<BillingChangeResponse>('POST', '/v1/app/billing/change', input);
  },
  /** Schedule cancellation at period end. Idempotent. */
  billingCancel() {
    return request<BillingCancelResponse>('POST', '/v1/app/billing/cancel', {});
  },
  /** Remove a scheduled cancellation. Idempotent. */
  billingResume() {
    return request<BillingResumeResponse>('POST', '/v1/app/billing/resume', {});
  },
  /** Mint the card-update transaction for the Paddle.js overlay. */
  billingCardUpdateTransaction() {
    return request<BillingCheckoutResponse>('POST', '/v1/app/billing/payment-method/update', {});
  },
  /** Card on file (read-through from Paddle). */
  billingPaymentMethod(signal?: AbortSignal) {
    return request<BillingPaymentMethodResponse>(
      'GET',
      '/v1/app/billing/payment-method',
      undefined,
      { signal },
    );
  },
  /** Recent transactions, newest first (read-through from Paddle). */
  billingHistory(signal?: AbortSignal) {
    return request<BillingHistoryResponse>('GET', '/v1/app/billing/history', undefined, {
      signal,
    });
  },
  /**
   * Invoice PDF as raw bytes. The API proxies Paddle's short-lived invoice URL
   * (cross-origin, no CORS) and streams the PDF with a download disposition,
   * so the UI saves the blob directly instead of fighting popup blockers with
   * tabs. Minted per click server-side; never cached.
   */
  async billingInvoiceDownload(transactionId: string): Promise<Blob> {
    const res = await fetch(
      `${API_BASE}/v1/app/billing/invoice/${encodeURIComponent(transactionId)}`,
      { credentials: 'include', headers: { Accept: 'application/pdf, application/json' } },
    );
    if (!res.ok) {
      let errPayload: ApiErrorPayload = {
        code: 'internal',
        message: res.statusText || 'Request failed',
      };
      try {
        const j = JSON.parse(await res.text()) as Record<string, unknown>;
        if (typeof j.code === 'string') errPayload = j as unknown as ApiErrorPayload;
      } catch {
        // Non-JSON error body; the generic payload stands.
      }
      throw new ApiError(res.status, errPayload);
    }
    return res.blob();
  },
  /**
   * Dev-only plan flip. Returns 501 in production (gate is server-side on
   * APP_ORIGIN). The dashboard only calls this from the mock billing flow on
   * /app/settings/billing, which itself only mounts when `import.meta.env.DEV`.
   * `simulate` toggles a fake scheduled cancel/resume on the current paid plan.
   */
  mockFlipPlan(input: {
    target_plan: PlanTier;
    cadence?: BillingCadence;
    simulate?: 'cancel_scheduled' | 'resume';
  }) {
    return request<MockFlipResponse>('POST', '/v1/app/billing/mock/flip', input);
  },

  // Beacons (no credentials; used by the channel Sales page "Test it" button + internal debug)
  beaconPageview(input: {
    workspace_public_id: string;
    click_id: string;
    url: string;
    referrer?: string;
    ts: number;
    session_id: string;
    screen?: { w: number; h: number; dpr?: number };
    ua?: string;
  }) {
    return request<undefined>('POST', `${apiOrigin()}/v1/beacon/pageview`, input, {
      contentType: 'text/plain',
    });
  },
  beaconFormSubmit(input: {
    workspace_public_id: string;
    click_id?: string;
    email: string;
    url: string;
    ts: number;
    session_id: string;
  }) {
    return request<undefined>('POST', `${apiOrigin()}/v1/beacon/form-submit`, input, {
      contentType: 'text/plain',
    });
  },
  beaconConversion(input: {
    workspace_public_id: string;
    click_id?: string;
    email?: string;
    transaction_id: string;
    amount_minor: number;
    currency: string;
    url: string;
    ts: number;
    session_id: string;
  }) {
    return request<undefined>('POST', `${apiOrigin()}/v1/beacon/conversion`, input, {
      contentType: 'text/plain',
    });
  },
};

/**
 * Beacons run from the dashboard origin (`app.clickt.link`) but POST to the
 * api origin directly so the `Origin` header is the customer-facing one when
 * we simulate an install. In dev, the Vite proxy rewrites cookies, but beacons
 * never send cookies, so we hit the API origin directly.
 */
function apiOrigin(): string {
  const origin = (import.meta.env.VITE_API_ORIGIN as string | undefined) ?? '';
  return origin || '';
}
